import React from 'react';

export type TallyProps = { directors: number; flash: number; mixed: number };

type TallyCell = { value: number; label: string; name: string; valueClass: string };

/** D / F / S node counts — the fastest read in the rail, as a strip along the foot of
 *  the stat block. The short label is a sighted-user affordance only: it stays
 *  aria-hidden and each cell carries an accessible group name (`role="group"` +
 *  `aria-label`) spelling out what it counts, so assistive tech hears "Mixed fleet
 *  nodes: 6", not "S-nodes". The container also carries `role="group"` alongside its
 *  `aria-label` — ARIA only exposes a label on an element with an explicit role, and a
 *  bare `<div>` is role=generic. */
export function Tally({ directors, flash, mixed }: TallyProps) {
  const cells: TallyCell[] = [
    { value: directors, label: 'D-nodes', name: 'Directors', valueClass: 'text-ink' },
    { value: flash, label: 'F-nodes', name: 'Flash nodes', valueClass: 'text-flash' },
    { value: mixed, label: 'S-nodes', name: 'Mixed fleet nodes', valueClass: 'text-brand-strong' },
  ];

  return (
    <div className="grid grid-cols-3 divide-x divide-hairline/15 border-t border-hairline/15" role="group" aria-label="Node tally">
      {cells.map(({ value, label, name, valueClass }) => (
        <div key={label} className="flex flex-col items-center px-2 py-3" role="group" aria-label={`${name}: ${value}`}>
          <span className={`font-code text-base font-semibold ${valueClass}`} aria-hidden="true">{value}</span>
          <span className="font-body text-tiny text-ink-muted" aria-hidden="true">{label}</span>
        </div>
      ))}
    </div>
  );
}
